import React, { useEffect, useState } from "react";
import Label from "../label";
import Numpad from "../numpad";
import BoltIcon from "@mui/icons-material/Bolt";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import TuneIcon from "@mui/icons-material/Tune";
import { useTranslation } from "react-i18next";

// Click behavior differs by stake (see numadd.jsx's handleType): "words"
// adds the bet immediately with no digit entry, "number" only arms the
// numpad below, and "step"/"mix" opens a picker modal. The icon + color
// tell them apart at a glance instead of every button looking identical.
export const STAKE_VARIANT = {
  words: {
    icon: BoltIcon,
    selected:
      "px-3 py-1 rounded-full text-[11px] md:text-xs font-semibold text-white bg-gradient-to-r from-emerald-500 to-green-600 ring-2 ring-emerald-300 shadow-md transition-colors",
    unselected:
      "px-3 py-1 rounded-full text-[11px] md:text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 shadow-sm hover:bg-emerald-100 transition-colors",
  },
  step: {
    icon: TuneIcon,
    selected:
      "px-3 py-1 rounded-full text-[11px] md:text-xs font-semibold text-brand-ink-onGold bg-gradient-to-r from-brand-gold-300 to-brand-gold-500 ring-2 ring-brand-gold-200 shadow-gold transition-colors",
    unselected:
      "px-3 py-1 rounded-full text-[11px] md:text-xs font-semibold text-brand-gold-700 bg-brand-gold-100 border border-brand-gold-300 shadow-sm hover:bg-brand-gold-200 transition-colors",
  },
  number: {
    icon: KeyboardIcon,
    selected:
      "px-3 py-1 rounded-full text-[11px] md:text-xs font-semibold text-brand-gold-200 bg-gradient-to-r from-brand-blue-600 to-brand-blue-800 ring-2 ring-brand-blue-300 shadow-md transition-colors",
    unselected:
      "px-3 py-1 rounded-full text-[11px] md:text-xs font-semibold text-brand-blue-700 bg-brand-blue-50 border border-brand-blue-200 shadow-sm hover:bg-brand-blue-100 transition-colors",
  },
};
STAKE_VARIANT.mix = STAKE_VARIANT.step;
export const getVariant = (stake) =>
  STAKE_VARIANT[stake] || STAKE_VARIANT.number;

export function GroupButtons({ groups, selectedGroupId, onSelect }) {
  return (
    <div className="flex w-[100%] flex-wrap gap-1.5">
      {groups.map((group) => (
        <button
          key={group.id}
          className={
            group.id === selectedGroupId
              ? "flex-1 min-w-[90px] justify-center bg-gradient-to-r from-brand-blue-700 to-brand-blue-900 rounded-full text-center p-2 text-white font-bold text-[11px] md:text-sm shadow-md ring-2 ring-brand-gold-300 transition-all"
              : "flex-1 min-w-[90px] justify-center bg-brand-blue-50 border border-brand-blue-200 rounded-full text-center p-2 text-brand-blue-700 font-semibold text-[11px] md:text-sm shadow-sm hover:bg-brand-blue-100 transition-colors"
          }
          onClick={() => onSelect(group.id)}
        >
          {group.name}
        </button>
      ))}
    </div>
  );
}

const isRunTop = (rewardType) => rewardType.code.startsWith("run_top");
const isRunBot = (rewardType) => rewardType.code.startsWith("run_bot");

// One shared card look for every reward type (no more per-digit color
// coding) — gold border matches the rest of the panel's theme.
const CARD_COLOR =
  "from-brand-blue-700 to-brand-blue-900 border-brand-gold-300";

export function RewardTypeButtons({
  rewardTypes,
  inputType,
  handleType,
  getRate,
}) {
  const { t } = useTranslation();
  const [runTab, setRunTab] = useState("top");

  // When a group carries both run_top* and run_bot* types, split them into
  // two tabs (left) and show only the active tab's types (right); any other
  // type in the group stays visible under both tabs.
  const hasRunTabs = rewardTypes.some(isRunTop) && rewardTypes.some(isRunBot);
  const visibleRewardTypes = hasRunTabs
    ? rewardTypes.filter((rewardType) =>
        runTab === "top" ? !isRunBot(rewardType) : !isRunTop(rewardType),
      )
    : rewardTypes;

  const renderRewardType = (rewardType) => {
    // Highlight only while actively armed for new input — having
    // existing bets of this type in the list (garr) no longer keeps
    // it highlighted once you move on to something else.
    const isSelected = inputType.includes(rewardType.code);
    const variant = getVariant(rewardType.stake);
    const Icon = variant.icon;
    // Strips the API's fixed decimal formatting ("92.00", "3.20") down to
    // how the mockup shows it ("92", "3.2").
    const rawRate = getRate ? getRate(rewardType.code) : undefined;
    const rate =
      rawRate === undefined || rawRate === null || rawRate === ""
        ? undefined
        : Number(rawRate).toString();
    return (
      <button
        key={rewardType.code}
        className={
          "flex items-stretch min-w-[150px] rounded-xl bg-gradient-to-br border-2 shadow-md transition-all overflow-hidden " +
          CARD_COLOR +
          (isSelected
            ? " ring-2 ring-brand-gold-300"
            : " opacity-90 hover:opacity-100")
        }
        value={rewardType.code}
        onClick={handleType}
      >
        <span className="flex-1 inline-flex items-center gap-1.5 px-3 py-2 text-[18px] font-bold text-white">
          <Icon sx={{ fontSize: 18 }} className="text-white/70" />
          {rewardType.name}
        </span>
        {rate !== undefined && (
          <span className="shrink-0 flex items-center px-3 bg-black/40 text-[18px] font-bold text-brand-gold-200">
            {rate}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex w-[100%] flex-col items-center gap-1.5 py-1">
      {hasRunTabs && (
        <div className="flex w-[100%] flex-row justify-center gap-1.5">
          {[
            { key: "top", label: t("numadd.runTabs.top") },
            { key: "bottom", label: t("numadd.runTabs.bottom") },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setRunTab(tab.key)}
              className={
                runTab === tab.key
                  ? "min-w-[72px] px-4 py-1.5 rounded-full text-xs font-bold text-brand-ink-onGold bg-gradient-to-r from-brand-gold-300 to-brand-gold-500 ring-2 ring-brand-gold-200 shadow-gold transition-colors"
                  : "min-w-[72px] px-4 py-1.5 rounded-full text-xs font-semibold text-brand-gold-200 bg-white/10 border border-brand-gold-300/40 hover:bg-white/20 transition-colors"
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap justify-center gap-1.5">
        {visibleRewardTypes.map(renderRewardType)}
      </div>
      <div className="flex flex-wrap justify-center gap-x-3 gap-y-0.5 text-[10px] text-white/50">
        <span className="inline-flex items-center gap-0.5">
          <BoltIcon sx={{ fontSize: 12 }} className="text-emerald-500" />
          {t("numadd.legend.instantAdd")}
        </span>
        <span className="inline-flex items-center gap-0.5">
          <KeyboardIcon sx={{ fontSize: 12 }} className="text-brand-blue-500" />
          {t("numadd.legend.typeDigits")}
        </span>
        <span className="inline-flex items-center gap-0.5">
          <TuneIcon sx={{ fontSize: 12 }} className="text-brand-gold-500" />
          {t("numadd.legend.openPicker")}
        </span>
      </div>
    </div>
  );
}

function ManualOrder({
  groups,
  garr,
  inputType,
  setInputType,
  handleType,
  getRate,
  betPrice,
  setBetPrice,
  number1,
  number2,
  number3,
  number4,
  number5,
  number6,
  numpadvisible,
  handleNumpad,
  handleCancelLastest,
  handleundo,
}) {
  const { t } = useTranslation();
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  useEffect(() => {
    if (selectedGroupId === null && groups.length > 0) {
      setSelectedGroupId(groups[0].id);
    }
  }, [groups, selectedGroupId]);

  // Switching group drops whatever was selected from the previous group,
  // since those codes no longer show up in the current group's buttons.
  const handleSelectGroup = (groupId) => {
    setSelectedGroupId(groupId);
    setInputType([]);
  };

  const selectedGroup = groups.find((group) => group.id === selectedGroupId);

  // The digit-entry slots (Label) show whichever "number"-stake code is
  // currently selected; that reward type's own `digit` field says how
  // many digits to display.
  const activeRewardType = groups
    .flatMap((group) => group.rewardTypes)
    .find(
      (rewardType) =>
        rewardType.stake === "number" && inputType.includes(rewardType.code),
    );
  const digitCount = activeRewardType ? activeRewardType.digit : 0;

  return (
    <>
      <div className="flex flex-row w-[100%] h-[auto] p-1">
        <GroupButtons
          groups={groups}
          selectedGroupId={selectedGroupId}
          onSelect={handleSelectGroup}
        />
      </div>

      <div className="w-[100%] flex flex-wrap flex-col h-auto justify-center mt-1 text-center bg-gradient-to-br from-brand-blue-900 via-brand-blue-800 to-brand-blue-900 items-center border-2 border-brand-gold-300 rounded-card shadow-card p-2 space-y-1">
        <span className="text-xs font-semibold text-brand-gold-300 tracking-wide">
          {t("numadd.selectedItems")}
        </span>

        <div className="flex flex-row items-center justify-center gap-2">
          <label
            htmlFor="bet-price-input"
            className="text-xs font-semibold text-brand-gold-200"
          >
            {t("numadd.setPrice")}
          </label>
          <input
            id="bet-price-input"
            type="number"
            min="1"
            inputMode="numeric"
            value={betPrice}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                setBetPrice("");
                return;
              }
              const parsed = parseInt(raw, 10);
              setBetPrice(isNaN(parsed) ? 10 : parsed);
            }}
            onBlur={() => {
              if (betPrice === "" || isNaN(betPrice) || betPrice < 1) {
                setBetPrice(10);
              }
            }}
            className="w-20 text-center text-sm font-bold text-slate-700 border border-slate-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-gold-300"
          />
        </div>

        <RewardTypeButtons
          rewardTypes={selectedGroup?.rewardTypes || []}
          inputType={inputType}
          garr={garr}
          handleType={handleType}
          getRate={getRate}
        />

        <Label
          className="label"
          digitCount={digitCount}
          number1={number1}
          number2={number2}
          number3={number3}
          number4={number4}
          number5={number5}
          number6={number6}
        />
      </div>

      <div className="w-[100%] h-auto justify-center items-center">
        <div className="div2">
          <Numpad
            numpadvisible={numpadvisible}
            disabled={digitCount === 0}
            handleNumpad={handleNumpad}
            handleCancelLastest={handleCancelLastest}
            handleundo={handleundo}
          />
        </div>
      </div>
    </>
  );
}

export default ManualOrder;
